import React, { useId, useRef } from 'react';
import EmojiPickerButton from './EmojiPickerButton';
import { useToast } from '../../shared/ToastProvider';
import '../TemplateManagement.css';

const HeaderSection = ({ formData, errors = {}, onChange }) => {
  const headerTitleRef = useRef(null);
  const toast = useToast();

  const updateHeader = (field, value) => {
    onChange({ [field]: value });
  };

  const handleHeaderTitleEmoji = (emoji) => {
    const input = headerTitleRef.current;
    const currentVal = formData.headerTitle || '';
    if (input) {
      const cursorPos = input.selectionStart ?? currentVal.length;
      const before = currentVal.substring(0, cursorPos);
      const after = currentVal.substring(cursorPos);
      updateHeader('headerTitle', before + emoji + after);
      requestAnimationFrame(() => {
        input.focus();
        const newPos = cursorPos + emoji.length;
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      updateHeader('headerTitle', currentVal + emoji);
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 4 * 1024 * 1024) {
      toast.error?.('File size must not exceed 4MB');
      return;
    }

    const dataUrl = await fileToDataUrl(file);
    onChange({
      [field + 'UrlPreview']: dataUrl,
      [field + 'UrlFile']: file
    });
  };

  const handleRemoveImage = (field) => {
    onChange({
      [field + 'UrlPreview']: '',
      [field + 'UrlFile']: null
    });
  };

  const logoInputId = useId();
  const bannerInputId = useId();

  const renderImageUploader = ({ field, label, inputId, guidelines = [] }) => {
    const url = formData[field + 'UrlPreview'];
    const hasImage = Boolean(url);

    return (
      <div className="emtpl-upload-card">
        <div className="emtpl-upload-row">
          <label
            className={`emtpl-upload-dropzone${hasImage ? ' has-image' : ''}`}
            htmlFor={inputId}
          >
            <input
              id={inputId}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, field)}
            />
            {hasImage ? (
              <div className="emtpl-upload-preview">
                <img src={url} alt={`${label} preview`} />
                <button
                  type="button"
                  className="emtpl-upload-remove"
                  aria-label={`Remove ${label}`}
                  title="Remove"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveImage(field);
                  }}
                >
                  &times;
                </button>
              </div>
            ) : (
              <div className="emtpl-upload-placeholder">
                <div className="emtpl-upload-icon">🖼️</div>
                <div>
                  <div className="emtpl-upload-title">{label}</div>
                  <div className="emtpl-upload-cta">Click to upload</div>
                </div>
              </div>
            )}
          </label>

          {!hasImage && guidelines.length > 0 && (
            <div className="emtpl-upload-guidelines">
              {guidelines.map((text, i) => (
                <span key={text + i}>• {text}</span>
              ))}
            </div>
          )}

          {hasImage && (
            <div className="emtpl-upload-actions">
              <label htmlFor={inputId} className="em-btn em-btn-secondary em-btn-sm">
                Change
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="emtpl-section">
      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <label className="em-label">Logo Image</label>
          {renderImageUploader({
            field: 'logo',
            label: 'Upload Logo',
            inputId: logoInputId,
            guidelines: ['Recommended: square PNG/SVG', 'Max size: 4MB']
          })}
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group">
          <label className="em-label">Logo Position</label>
          <select
            className="em-select"
            value={formData.headerLogoPosition}
            onChange={(e) => updateHeader('headerLogoPosition', e.target.value)}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
          <span className="em-helper">Alignment of the logo in header</span>
        </div>

        <div className="emtpl-group">
          <label className="em-label">Background Color</label>
          <div className="emtpl-color-wrapper">
            <input
              type="color"
              className="emtpl-color-picker"
              value={formData.headerBackgroundColor}
              onChange={(e) => updateHeader('headerBackgroundColor', e.target.value)}
            />
            <input
              type="text"
              className="em-input"
              value={formData.headerBackgroundColor}
              onChange={(e) => updateHeader('headerBackgroundColor', e.target.value)}
              placeholder="#ffffff"
              style={{ flex: 1 }}
            />
          </div>
          <span className="em-helper">Header background color</span>
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="em-label required" style={{ marginBottom: 0 }}>Header Title</label>
            <EmojiPickerButton onSelect={handleHeaderTitleEmoji} />
          </div>

          <div className="emtpl-var-toolbar">
            <span className="emtpl-var-toolbar-label">Insert:</span>
            {formData.variables?.map((variable) => (
              <button
                key={variable.key}
                type="button"
                className="emtpl-var-btn"
                onClick={() => {
                  const input = headerTitleRef.current;
                  const currentVal = formData.headerTitle || '';
                  if (input) {
                    const cursorPos = input.selectionStart || currentVal.length;
                    const textBefore = currentVal.substring(0, cursorPos);
                    const textAfter = currentVal.substring(cursorPos);
                    updateHeader('headerTitle', textBefore + `{{${variable.key}}}` + textAfter);
                  } else {
                    updateHeader('headerTitle', currentVal + `{{${variable.key}}}`);
                  }
                }}
                title={variable.description || `Insert {{${variable.key}}}`}
              >
                {variable.key}
              </button>
            ))}
          </div>

          <input
            ref={headerTitleRef}
            type="text"
            className={`em-input${errors.headerTitle ? ' em-input-error' : ''}`}
            data-header-title-input="true"
            value={formData.headerTitle}
            onChange={(e) => updateHeader('headerTitle', e.target.value)}
            placeholder="e.g., Welcome!"
          />
          {errors.headerTitle && <span className="em-error-text">{errors.headerTitle}</span>}
          <span className="em-helper">Title text shown in email header (supports variables)</span>
        </div>
      </div>

      <div className="emtpl-row">
        <div className="emtpl-group full-width">
          <label className="em-label">Banner Image</label>
          {renderImageUploader({
            field: 'bannerImage',
            label: 'Event Banner',
            inputId: bannerInputId,
            guidelines: [
              'Recommended: 1200 × 675px (16:9)',
              'Minimum: 600 × 338px',
              'Max size: 4MB | Formats: JPEG, PNG'
            ]
          })}
          <span className="em-helper">Optional banner image shown below the logo</span>
        </div>
      </div>
    </div>
  );
};

export default HeaderSection;
